// Components==============
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import AddUserModal from 'components/UI/AddUserModal/AddUserModal';
import Avatar from 'components/UI/Avatar/Avatar';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import ReflectionAvatar from 'components/UI/ReflectionAvatar/ReflectionAvatar';
import { AnimatePresence } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import React, { useContext, useState } from 'react';
import { ReflectionItemContext } from '../../ReflectionItem';
import './ReflectionOverviewArea.module.scss';
// =========================

export default function ReflectionOverviewArea({
  inactive,
  endPoint,
  reflections,
}: {
  inactive: boolean;
  endPoint?: boolean;
  reflections: (ReflectionEntity | undefined)[];
}) {
  const translate = useTranslate();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const peerReflections = reflections.filter(
    (r) =>
      r?.user.roles[0]?.name === 'Leerling' ||
      r?.user.roles[0]?.name === 'Leerling Portfolio'
  );

  const teacherReflections = reflections.filter(
    (r) =>
      r?.user.roles[0]?.name === 'Docent' ||
      r?.user.roles[0]?.name === 'Extern' ||
      r?.user.roles[0]?.name === 'super-admin'
  );

  const [selected, setSelected] = useState(
    peerReflections?.[0]?.id || teacherReflections?.[0]?.id || 0
  );

  const activeReflection = reflections.find((r) => r?.id === selected);

  const lang = useRedux(
    (state) => state.localize?.languages.find((l) => l.active)?.code
  ) as 'nl' | 'en' | undefined;

  const { handleInvitePeer } = useContext(ReflectContext);

  const { activity } = useContext(ReflectionItemContext);
  const processId = activity.id;

  const message = (user: UserEntity) =>
    reflections.find((r) => r?.created_by === user.id) !== undefined
      ? `  <b>(${translate('reflection.already-invited')})</b>`
      : '';

  if (!lang) return <> </>;

  return (
    <>
      <div styleName={`wrapper ${inactive ? 'inactive' : ''}`}>
        <div styleName="reflections">
          {!endPoint && (
            <div styleName="avatars-wrapper">
              <strong>
                {translate('reflection.terms.peer-reflections')}:{' '}
              </strong>
              <div styleName="avatars">
                {peerReflections.map(
                  (r) =>
                    !!r && (
                      <ReflectionAvatar
                        key={r.id}
                        reflectionComplete={r.finished}
                        active={r.id === selected}
                        onClick={() => setSelected(r.id)}
                        user={r.user}
                        withMargin
                      />
                    )
                )}
                <CircleButton
                  color="grey-blue"
                  icon={faPlus}
                  onClick={() => setModalIsOpen(true)}
                />
              </div>
            </div>
          )}
          {!!teacherReflections.length && (
            <div styleName="avatars-wrapper" style={{ justifySelf: 'end' }}>
              <strong>
                {translate(
                  `reflection.terms.${
                    endPoint ? 'reflections' : 'teacher-reflections'
                  }`
                )}
                :
              </strong>
              <div styleName="avatars">
                {teacherReflections.map(
                  (r) =>
                    !!r && (
                      <ReflectionAvatar
                        key={r.id}
                        reflectionComplete={r.finished}
                        active={r.id === selected}
                        onClick={() => setSelected(r.id)}
                        user={r.user}
                        withMargin
                      />
                    )
                )}
              </div>
            </div>
          )}
        </div>
        <div styleName={`reflection ${selected ? 'with-content' : ''}`}>
          {selected ? (
            <>
              <div styleName="user">
                <Avatar user={activeReflection?.user} />
                <strong>
                  {activeReflection?.user.first_name}{' '}
                  {activeReflection?.user.last_name}
                </strong>
              </div>
              {activeReflection?.note?.length ? (
                activeReflection.note.map((note, i) => (
                  <div key={i}>
                    {!!activeReflection.note &&
                      activeReflection.note?.length > 1 && (
                        <strong>{note.question[lang]}</strong>
                      )}
                    <p>{note.answer}</p>
                  </div>
                ))
              ) : (
                <div>
                  <strong>{translate('reflection.not-reflected')}</strong>
                  <p>
                    {translate('general.terms.as-soon')}{' '}
                    {activeReflection?.user.first_name}{' '}
                    {translate('reflection.reflected-results')}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <strong>
                {translate(
                  `reflection.${endPoint ? 'no-reflection' : 'invite-people'}`
                )}
              </strong>
              <p>{translate('reflection.insights-process')}</p>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {modalIsOpen && (
          <AddUserModal
            title={translate('reflection.add-user.title')}
            description={translate('reflection.add-user.description')}
            onSubmit={(userId) =>
              !!processId && handleInvitePeer(userId, processId)
            }
            setModalIsOpen={setModalIsOpen}
            message={message}
            type="feedback"
          />
        )}
      </AnimatePresence>
    </>
  );
}
